// RS product grid: loads the next page of products (Section Rendering API) and appends it.
// Mode "button": on click. Mode "auto": when the button comes near the viewport (the button
// stays as a fallback, e.g. if a request fails).
// With tabs (Tab blocks) every tab panel has its own list and button: "load more" fetches the
// next page of that tab only and takes its list from the response ([data-rs-pg-list="<key>"]).
// Also used by RS collection + banner (one list, no panels, data-rs-pg-list without a value).
if (!customElements.get('rs-product-grid')) {
  class RsProductGridPanel {
    constructor(grid, panel) {
      this.grid = grid;
      this.panel = panel;
      this.list = panel.querySelector('[data-rs-pg-list]');
      this.button = panel.querySelector('[data-rs-pg-more]');
      this.status = panel.querySelector('[data-rs-pg-status]');
      if (!this.list || !this.button) return;

      this.onClick = () => this.loadMore();
      this.button.addEventListener('click', this.onClick);

      if (grid.dataset.mode === 'auto' && 'IntersectionObserver' in window) {
        // A hidden panel (closed tab) never intersects, so only the open tab loads.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this.button);
      }
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    // Called when the tab opens: an observed button that was hidden is checked again.
    refresh() {
      if (this.observer && this.button) {
        this.observer.unobserve(this.button);
        this.observer.observe(this.button);
      }
    }

    async loadMore() {
      const nextPage = this.list && this.list.dataset.nextPage;
      if (this.loading || !nextPage) return;

      this.loading = true;
      this.button.setAttribute('aria-busy', 'true');

      try {
        const { url, sectionId } = this.grid.dataset;
        const response = await fetch(`${url}?section_id=${encodeURIComponent(sectionId)}&page=${nextPage}`);
        if (!response.ok) throw new Error(response.status);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const key = this.list.dataset.rsPgList;
        const newList = html.querySelector(`[data-rs-pg-list="${key}"]`);
        if (!newList) throw new Error('No product list in response');

        const items = Array.from(newList.children);
        this.list.append(...items);

        if (newList.dataset.nextPage) {
          this.list.dataset.nextPage = newList.dataset.nextPage;
        } else {
          delete this.list.dataset.nextPage;
          // The button goes away: keep keyboard focus in the grid (first new product).
          const hadFocus = document.activeElement === this.button;
          this.finish();
          const firstLink = items[0] && items[0].querySelector('a[href]');
          if (hadFocus && firstLink) firstLink.focus();
        }

        if (this.status) this.status.textContent = `${this.grid.dataset.loadedText} (${items.length})`;
      } catch (error) {
        console.error('RS product grid: could not load more products', error);
        // Automatic loading stops after an error; the button remains for a manual retry.
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      } finally {
        this.loading = false;
        if (this.button) this.button.removeAttribute('aria-busy');
      }

      // Auto mode: if the button is still in view after loading (short page), keep going.
      this.refresh();
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      // Remove the button; the wrapper too unless it still holds the "view all" button.
      const wrapper = this.button.closest('.rs-pg__more');
      this.button.remove();
      if (wrapper && !wrapper.children.length) wrapper.remove();
      this.button = null;
    }
  }

  class RsProductGrid extends HTMLElement {
    connectedCallback() {
      // No panels (e.g. RS collection + banner): the whole element is the one panel.
      const panels = Array.from(this.querySelectorAll('[data-rs-pg-panel]'));
      this.panels = (panels.length ? panels : [this]).map((panel) => new RsProductGridPanel(this, panel));
      this.tabs = Array.from(this.querySelectorAll('[data-rs-pg-tab]'));
      if (!this.tabs.length) return;

      this.onTabClick = (event) => {
        const tab = event.target.closest('[data-rs-pg-tab]');
        if (tab) this.select(this.tabs.indexOf(tab));
      };
      this.onTabKey = (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        const last = this.tabs.length - 1;
        const next = {
          ArrowRight: index === last ? 0 : index + 1,
          ArrowLeft: index === 0 ? last : index - 1,
          Home: 0,
          End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.select(next, true);
      };
      // Theme editor: selecting a Tab block opens its tab.
      this.onBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      };

      const tablist = this.querySelector('[role="tablist"]');
      tablist.addEventListener('click', this.onTabClick);
      tablist.addEventListener('keydown', this.onTabKey);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      (this.panels || []).forEach((panel) => panel.destroy());
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const open = i === index;
        tab.setAttribute('aria-selected', open ? 'true' : 'false');
        tab.tabIndex = open ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !open;
      });
      if (focus) this.tabs[index].focus();
      const panel = this.panels.find((p) => p.panel.id === this.tabs[index].getAttribute('aria-controls'));
      if (panel) panel.refresh();
    }
  }

  customElements.define('rs-product-grid', RsProductGrid);
}
